/**
 * Dice formula parsing and rolling.
 *
 * Rolls happen on the server so the DM's feed shows a number nobody could have
 * re-rolled, and so a refresh can never change a result. The RNG is injectable
 * purely so the behaviour can be tested.
 */

export interface DiceTerm {
  count: number
  sides: number
  /** Keep the highest or lowest N, as in 4d6kh3 (roll four, keep best three). */
  keep?: { mode: 'h' | 'l'; n: number }
  /** Terms after a minus sign subtract from the total. */
  negative: boolean
}

export interface ParsedFormula {
  terms: DiceTerm[]
  modifier: number
  /** Normalised text, e.g. "2d6 + 4". */
  normalised: string
}

export interface RolledDie {
  sides: number
  value: number
  /** True when a keep-highest/lowest rule discarded this die. */
  dropped: boolean
  negative: boolean
}

export type Advantage = 'none' | 'advantage' | 'disadvantage'
export type Outcome = 'normal' | 'crit' | 'fumble'

export interface RollResult {
  formula: string
  dice: RolledDie[]
  modifier: number
  total: number
  advantage: Advantage
  outcome: Outcome
  /** The kept d20 face, when this was a d20 roll. Drives crit and fumble. */
  natural: number | null
}

export class DiceError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'DiceError'
  }
}

const MAX_DICE = 100
const MAX_SIDES = 1000

/**
 * Accepts things people actually type: "2d6+4", "d20", "4d6kh3", "1d8 + 1d6 - 2".
 * Whitespace and case are irrelevant.
 */
export function parseFormula(raw: string): ParsedFormula {
  const text = raw.trim().toLowerCase().replace(/\s+/g, '')
  if (!text) throw new DiceError('Enter something to roll, like 1d20+5')
  if (!/^[-+]?[\ddhkl+-]+$/.test(text)) {
    throw new DiceError(`"${raw.trim()}" is not a dice formula. Try something like 2d6+3.`)
  }

  const terms: DiceTerm[] = []
  let modifier = 0

  // Split into signed chunks: "2d6+4-1d4" -> ["+2d6", "+4", "-1d4"]
  const signedText = text.replace(/^([^+-])/, '+$1')
  const chunks = signedText.match(/[+-][^+-]+/g)
  if (!chunks) throw new DiceError(`Could not read "${raw.trim()}"`)

  // The chunks must account for every character, otherwise trailing or doubled
  // signs ("1d20++") would be quietly dropped instead of rejected.
  if (chunks.join('') !== signedText) {
    throw new DiceError(`"${raw.trim()}" has a stray + or - in it`)
  }

  for (const chunk of chunks) {
    const negative = chunk[0] === '-'
    const body = chunk.slice(1)
    if (!body) throw new DiceError(`Could not read "${raw.trim()}"`)

    const dice = body.match(/^(\d*)d(\d+)(?:k([hl])(\d+))?$/)
    if (dice) {
      const count = dice[1] ? Number(dice[1]) : 1
      const sides = Number(dice[2])

      if (count < 1) throw new DiceError('You need to roll at least one die')
      if (count > MAX_DICE) throw new DiceError(`That is more than ${MAX_DICE} dice`)
      if (sides < 2) throw new DiceError('A die needs at least two sides')
      if (sides > MAX_SIDES) throw new DiceError(`A die cannot have more than ${MAX_SIDES} sides`)

      const term: DiceTerm = { count, sides, negative }
      if (dice[3]) {
        const n = Number(dice[4])
        if (n < 1 || n > count) throw new DiceError(`You cannot keep ${n} of ${count} dice`)
        term.keep = { mode: dice[3] as 'h' | 'l', n }
      }
      terms.push(term)
      continue
    }

    if (/^\d+$/.test(body)) {
      modifier += negative ? -Number(body) : Number(body)
      continue
    }

    throw new DiceError(`Could not read "${body}" in "${raw.trim()}"`)
  }

  if (terms.length === 0) throw new DiceError('That has no dice in it — try 1d20 first')

  return { terms, modifier, normalised: formatFormula(terms, modifier) }
}

function formatFormula(terms: DiceTerm[], modifier: number): string {
  const parts = terms.map((term, index) => {
    const keep = term.keep ? `k${term.keep.mode}${term.keep.n}` : ''
    const body = `${term.count}d${term.sides}${keep}`
    if (index === 0) return term.negative ? `-${body}` : body
    return `${term.negative ? '-' : '+'} ${body}`
  })
  if (modifier !== 0) parts.push(`${modifier < 0 ? '-' : '+'} ${Math.abs(modifier)}`)
  return parts.join(' ')
}

export type Rng = (sides: number) => number

/** Uniform over 1..sides, via rejection sampling so the tail is not biased. */
export const cryptoRng: Rng = (sides) => {
  const limit = Math.floor(0xffffffff / sides) * sides
  const buffer = new Uint32Array(1)
  let value: number
  do {
    crypto.getRandomValues(buffer)
    value = buffer[0]!
  } while (value >= limit)
  return (value % sides) + 1
}

export function rollFormula(
  raw: string,
  options: { advantage?: Advantage; rng?: Rng } = {},
): RollResult {
  const advantage = options.advantage ?? 'none'
  const rng = options.rng ?? cryptoRng
  let parsed = parseFormula(raw)

  // Advantage is expressed by rewriting the single d20 as 2d20 keep high/low,
  // so the feed shows both faces and one of them visibly dropped.
  if (advantage !== 'none') {
    const d20 = parsed.terms.find((term) => term.sides === 20 && term.count === 1 && !term.keep)
    if (!d20) {
      throw new DiceError('Advantage and disadvantage only apply to a single d20')
    }
    d20.count = 2
    d20.keep = { mode: advantage === 'advantage' ? 'h' : 'l', n: 1 }
    parsed = { ...parsed, normalised: formatFormula(parsed.terms, parsed.modifier) }
  }

  const dice: RolledDie[] = []
  let total = parsed.modifier

  for (const term of parsed.terms) {
    const rolled = Array.from({ length: term.count }, () => rng(term.sides))

    let keptIndexes: Set<number>
    if (term.keep) {
      const order = rolled
        .map((value, index) => ({ value, index }))
        .sort((a, b) => (term.keep!.mode === 'h' ? b.value - a.value : a.value - b.value))
      keptIndexes = new Set(order.slice(0, term.keep.n).map((entry) => entry.index))
    } else {
      keptIndexes = new Set(rolled.map((_, index) => index))
    }

    rolled.forEach((value, index) => {
      const dropped = !keptIndexes.has(index)
      dice.push({ sides: term.sides, value, dropped, negative: term.negative })
      if (!dropped) total += term.negative ? -value : value
    })
  }

  // A crit is decided by the d20 that counted, never by a dropped one.
  const keptD20 = dice.filter((die) => die.sides === 20 && !die.dropped)
  const natural = keptD20.length === 1 ? keptD20[0]!.value : null
  const outcome: Outcome = natural === 20 ? 'crit' : natural === 1 ? 'fumble' : 'normal'

  return { formula: parsed.normalised, dice, modifier: parsed.modifier, total, advantage, outcome, natural }
}

/** "+5" / "-1" / "" — for building a formula out of a bonus. */
export const signed = (value: number): string => (value < 0 ? `${value}` : `+${value}`)

export const formulaFor = (bonus: number, die = 'd20'): string =>
  bonus === 0 ? `1${die}` : `1${die}${signed(bonus)}`
